import { useEffect, useRef, useCallback } from "react";
import { toast } from "sonner";

const INACTIVITY_TIMEOUT_MS = 60_000 * 180; // 3 hours
const WARNING_BEFORE_MS = 15_000 * 3;     // 3 minutes before logout

type UseInactivityTimeoutOptions = {
  onLogout: () => void;
};

const ACTIVITY_EVENTS: (keyof WindowEventMap)[] = [
  "mousemove",
  "mousedown",
  "keydown",
  "touchstart",
  "scroll",
  "click",
];

export function useInactivityTimeout({ onLogout }: UseInactivityTimeoutOptions) {
  const logoutTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const warningTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const warningToastIdRef = useRef<string | number | null>(null);

  const clearTimers = useCallback(() => {
    if (logoutTimerRef.current) {
      clearTimeout(logoutTimerRef.current);
      logoutTimerRef.current = null;
    }
    if (warningTimerRef.current) {
      clearTimeout(warningTimerRef.current);
      warningTimerRef.current = null;
    }
  }, []);

  const dismissWarning = useCallback(() => {
    if (warningToastIdRef.current !== null) {
      toast.dismiss(warningToastIdRef.current);
      warningToastIdRef.current = null;
    }
  }, []);

  const startTimers = useCallback(() => {
    clearTimers();
    dismissWarning();

    // Schedule a warning toast 15 seconds before logout
    warningTimerRef.current = setTimeout(() => {
      warningToastIdRef.current = toast.warning(
        "Session Expiring Soon",
        {
          description: "You will be logged out in 15 seconds due to inactivity.",
          duration: WARNING_BEFORE_MS,
        }
      );
    }, INACTIVITY_TIMEOUT_MS - WARNING_BEFORE_MS);

    // Schedule the actual logout
    logoutTimerRef.current = setTimeout(() => {
      dismissWarning();
      toast.info("Session Expired", {
        description: "You have been logged out due to inactivity.",
      });
      onLogout();
    }, INACTIVITY_TIMEOUT_MS);
  }, [clearTimers, dismissWarning, onLogout]);

  const resetTimers = useCallback(() => {
    startTimers();
  }, [startTimers]);

  useEffect(() => {
    // Start timers on mount
    startTimers();

    // Reset timers on any user activity
    ACTIVITY_EVENTS.forEach((event) => {
      window.addEventListener(event, resetTimers, { passive: true });
    });

    return () => {
      clearTimers();
      dismissWarning();
      ACTIVITY_EVENTS.forEach((event) => {
        window.removeEventListener(event, resetTimers);
      });
    };
  }, [startTimers, resetTimers, clearTimers, dismissWarning]);
}
